import React, {useCallback, useContext, useMemo, useState, useEffect} from "react";
import {groupBy, isNumber} from "lodash";
import {ConfigProvider, Form, message, Select, Spin, Tabs, Tag, Tooltip} from "antd";
import {CrownFilled, QuestionCircleOutlined} from "@ant-design/icons";
import {EditSubAdminModal} from "./EditSubAdminModal";
import {CompanyProfileManageAdmins} from "./CompanyProfileManageAdmins";
import {CompanyBudgetHistoryModal} from "./CompanyBudgetHistoryModal";
import {CompanyInvoiceManage} from "./CompanyInvoiceManage";
import {CompanyBudgetLabel} from "../CompanyBudgetLabel";
import {ResetPasswordModal} from "../ResetPasswordModal";
import columns from "../../admin/AdminOrders/columns";
import {AppContext} from "../../AppContext";
import {RoundedTable, StyledButton} from "../../components";
import {useRequest} from "../../utils/hooks";
import {UpdateCompanyDetailsModal} from "../../marketplace/UpdateCompanyDetailsModal";
import {CompanyOrdersWrapper} from "../orders";
import {attachCountryStateAndCity} from "../../wizards/wizardUtils";
import {HttpClient} from "../../http/HttpClient";
import {Languages} from "../../auth/Login";
import {EventBus} from "../../bus/EventBus";
import {getTotalBudget} from "../../utils/calculationUtils";
import {SavedOrderTable} from "../../components/savedOrder";
import "./company-profile.css";
import {PHONE_NUMBER_ERROR_VALIDATION} from "../../marketplace/consts";
import common from "@well-b/well-b-shared";
import {CompanyPermissionModal} from "../orders/CompanyPermissionModal.jsx";
import {StringBuilder} from "../../AppUtils.js";
import {ReportManagement} from "./Reports/ReportManagement.jsx";
import {reportsOptions, reportsOptionsToText} from "./Reports/consts.js";
import {ReportManagementSubAdminUsage} from "./Reports/reportManagementSubAdminUsage/ReportManagementSubAdminUsage.jsx";
import {openWindow} from "../../http/WindowOpener.jsx";
import classNames from "classnames";
import {replaceCompanyAccountProfile} from "../../api/users/users.js";
import {updateMeCompanyFinanceContact} from "../../api/company/index.jsx";
import {CompanyBriefsWrapper} from "./CompanyBriefs";
import {useLocation} from "react-router";

export const CompanyAccountProfile = ({history, match}) => {
    const {me, company, services, fetchCompany} = useContext(AppContext);

    const [orders = [], isOrdersLoading] = useRequest(`/api/companies/orders/${match.params.companyId}/open`);

    const [changePasswordModalVisibility, setChangePasswordModalVisibility] = useState(false);
    const [updateCompanyInfoModalVisibility, setUpdateCompanyInfoModalVisibility] = useState(false);
    const [updateSubAdminInfo, setUpdateSubAdminInfo] = useState(null);
    const [budgetHistoryModalVisibility, setBudgetHistoryModalVisibility] = useState(false);
    const [isCompanyPermissionModalOpen, setIsCompanyPermissionModalOpen] = useState(false);
    const [isLoadingChangingAccountProfile, setIsLoadingChangingAccountProfile] = useState(false);

    const location = useLocation();
    const {activeKey} = location.state || {};
    const [defaultActiveKey, setDefaultActiveKey] = useState(activeKey || "1");

    useEffect(() => {
        if (!!activeKey) {
            setDefaultActiveKey(activeKey);
        }
    }, [activeKey]);

    const isManager = useMemo(() => me?.isCompanyMaster || me?.manageDepartments?.length, [me]);

    const isAllowedToInvoiceTab = useMemo(() => {
        if (!me?.features?.finance_contact_manage) {
            return false;
        }

        if (me?.isCompanyMaster) {
            return true;
        }

        return company?.separateSubAdminInvoices;
    }, [me?.features?.finance_contact_manage, me?.isCompanyMaster, company?.separateSubAdminInvoices]);

    const [companyBudgets = [], loadingCompanyBudgets, _, fetchCompanyBudgets] = useRequest(
        "/api/budget/company",
        "get",
        null,
        [],
        !!isManager
    );

    const [workspaces = [], isLoadingUserWorkspaces] = useRequest("/api/users/workspaces/me");

    const companyBudgetsByUserId = useMemo(
        () => groupBy(companyBudgets?.budgets ?? [], budget => budget.userId),
        [companyBudgets]
    );

    const remainingBudgets = useMemo(
        () =>
            isManager
                ? companyBudgets?.totalBudget
                : getTotalBudget(
                      me.budget,
                      services?.map(({serviceId}) => serviceId)
                  ),
        [isManager, companyBudgets, me.budget, services]
    );

    const onSaveCompanyDetails = useCallback(
        async values => {
            const valuesToUpdate = attachCountryStateAndCity(values);
            setUpdateCompanyInfoModalVisibility(false);
            const results = await HttpClient.safePost(
                `/api/companies/${company.companyId}/updateCompanyOrderDetails`,
                valuesToUpdate
            );

            if (results?.error) {
                message.error("קרתה שגיאה בשמירת פרטי החברה, יש לרפרש את העמוד לנסות שנית");
            } else {
                EventBus.trigger("company:update", results);

                message.success("הפרטים נשמרו בהצלחה!", 5);
            }
        },
        [company]
    );

    const onSaveSubAdmin = useCallback(
        async values => {
            const {budget, ...newInfo} = values;

            const user = {...me, ...newInfo};

            await HttpClient.safePost(`/api/companies/me/employees/${user.userId}`, user);

            setUpdateSubAdminInfo(null);
            EventBus.trigger("me:update", {...user});
        },
        [me]
    );

    const updateBudget = useCallback(() => {
        EventBus.trigger("me:updateBudget");
        fetchCompanyBudgets();
    }, [fetchCompanyBudgets]);

    const companyInitValues = useMemo(
        () => ({
            contactName: company?.contactName,
            contactEmail: company?.contactEmail,
            phone: company?.phone
        }),
        [company]
    );

    const onCompanyPermissionClick = useCallback(() => {
        setIsCompanyPermissionModalOpen(true);
    }, []);

    const onCompanyPermissionClose = useCallback(() => {
        setIsCompanyPermissionModalOpen(false);
    }, []);

    const onPermissionSave = useCallback(
        async permissions => {
            onCompanyPermissionClose();
            const updatedCompany = await HttpClient.safePut(
                `/api/companies/${company.companyId}/updateCompanyPermissions`,
                {
                    needManagerConfirm: !!permissions.needManagerConfirm,
                    allowBudgetTransferByManageDepartment: !!permissions.allowBudgetTransferByManageDepartment
                }
            );
            if (updatedCompany.error) {
                message.error("שגיאה קרתה במהלך שמירת הרשאות, אנא נסה שנית");
                return;
            }
            fetchCompany();
            message.success("הרשאות נשמרו בהצלחה");
        },
        [company, onCompanyPermissionClose, fetchCompany]
    );

    const reports = useMemo(() => {
        return [
            {
                key: 1,
                title: reportsOptionsToText[reportsOptions.extractCompanySubAdminReport],
                shouldShow: me?.features?.report_subadmin_usage && me?.isCompanyMaster,
                component: <ReportManagementSubAdminUsage companyId={match?.params?.companyId} />
            }
        ].filter(reportOption => reportOption.shouldShow);
    }, [me?.isCompanyMaster, match?.params?.companyId, me?.features?.report_subadmin_usage]);

    const onEditOrder = useCallback(order => {
        openWindow(`/events/${order.eventId}/edit`);
    }, []);

    const onChangeProfile = useCallback(
        async selectedAccountProfileUserId => {
            if (me?.userId === selectedAccountProfileUserId) {
                return;
            }
            setIsLoadingChangingAccountProfile(true);
            await replaceCompanyAccountProfile(selectedAccountProfileUserId);
            setIsLoadingChangingAccountProfile(false);
            window.location.assign("/dashboard");
        },
        [me?.userId]
    );

    const onSaveMeCompanyFinanceContact = useCallback(
        async financeContact => {
            await updateMeCompanyFinanceContact(me, financeContact);
        },
        [me]
    );

    return (
        <div className="company-profile-layout">
            <ConfigProvider direction="rtl">
                <CompanyPermissionModal
                    visible={isCompanyPermissionModalOpen}
                    onClose={onCompanyPermissionClose}
                    onSave={onPermissionSave}
                />
                {me.isCompanyMaster ? (
                    <UpdateCompanyDetailsModal
                        title="עריכת פרטי חשבון"
                        onSave={onSaveCompanyDetails}
                        visible={updateCompanyInfoModalVisibility}
                        onClose={() => setUpdateCompanyInfoModalVisibility(false)}
                        initFormValues={companyInitValues}
                        contactSection={{
                            title: "פרטי איש קשר",
                            fields: [
                                {
                                    name: ["contactName"],
                                    initialValue: ["contactName"],
                                    label: "שם מלא",
                                    rules: [
                                        {
                                            required: true,
                                            message: "יש להזין שם מלא"
                                        }
                                    ]
                                },
                                {
                                    name: ["phone"],
                                    initialValue: ["phone"],
                                    label: "טלפון",
                                    type: "number",
                                    rules: [
                                        {
                                            required: true,
                                            message: "יש להזין מספר טלפון"
                                        },
                                        {
                                            validator: (_, value) => {
                                                return common.isValidPhoneMobileNumber(value)
                                                    ? Promise.resolve()
                                                    : Promise.reject(PHONE_NUMBER_ERROR_VALIDATION);
                                            }
                                        }
                                    ]
                                }
                            ]
                        }}
                    />
                ) : (
                    <EditSubAdminModal
                        visible={!!updateSubAdminInfo}
                        onSave={onSaveSubAdmin}
                        mainServices={services}
                        onClose={() => setUpdateSubAdminInfo(null)}
                        subAdmin={updateSubAdminInfo}
                        disables={["allowedPaymentsMethods", "allowedServices", "department", "manageDepartments"]}
                    />
                )}
                <CompanyBudgetHistoryModal
                    history={history}
                    visible={budgetHistoryModalVisibility}
                    onClose={() => setBudgetHistoryModalVisibility(null)}
                />
                <ResetPasswordModal
                    language={Languages.Hebrew}
                    title="שינוי סיסמה"
                    visible={changePasswordModalVisibility}
                    onClose={() => setChangePasswordModalVisibility(false)}
                />
                <div className="company-profile-title-layout">
                    <div style={{display: "flex", justifyContent: "flex-end", flexDirection: "row"}}>
                        <span className="company-profile-header" style={{marginLeft: 10}}>
                            {"פרופיל"}
                        </span>
                        <span className="company-profile-header">{company?.name}</span>
                    </div>
                    {!!me?.isCompanyMaster && me?.features?.company_account_permission_btn && (
                        <div>
                            <StyledButton className="company-permission-btn" onClick={onCompanyPermissionClick}>
                                הרשאות
                            </StyledButton>
                        </div>
                    )}
                </div>
                <Tabs
                    className="company-profile-tabs"
                    defaultActiveKey={defaultActiveKey}
                    items={[
                        {
                            label: "החשבון שלי",
                            key: "1",
                            children: (
                                <>
                                    <div className="company-profile-general-info-section">
                                        <div className="company-profile-info-box-layout">
                                            <div style={{display: "flex", flexDirection: "column", width: "100%"}}>
                                                <span
                                                    className="company-profile-header"
                                                    style={{fontSize: 24, direction: "rtl"}}>
                                                    {"פרטי חשבון"}
                                                </span>

                                                <Form.Item>
                                                    <Select
                                                        className="company-account-profile-select"
                                                        onSelect={onChangeProfile}
                                                        loading={isLoadingUserWorkspaces}
                                                        value={workspaces?.length ? me?.userId : undefined}
                                                        popupClassName="company-account-profile-select-option-container"
                                                        options={
                                                            workspaces?.map(
                                                                ({
                                                                    userId,
                                                                    companyName,
                                                                    businessNumber,
                                                                    firstName,
                                                                    isCompanyMaster,
                                                                    manageDepartments
                                                                }) => ({
                                                                    value: userId,
                                                                    label: (
                                                                        <div className="account-profile-label">
                                                                            <div className="account-profile-label-header">
                                                                                <UserCrown
                                                                                    firstName={firstName}
                                                                                    isCompanyMaster={isCompanyMaster}
                                                                                    manageDepartments={
                                                                                        manageDepartments
                                                                                    }
                                                                                />
                                                                                {userId === me?.userId && (
                                                                                    <Tag color="pink">מחובר</Tag>
                                                                                )}
                                                                            </div>
                                                                            {new StringBuilder(companyName, " | ")
                                                                                .append(businessNumber)
                                                                                .toString()}
                                                                        </div>
                                                                    ),
                                                                    name: (
                                                                        <UserCrown
                                                                            firstName={firstName}
                                                                            isCompanyMaster={isCompanyMaster}
                                                                            manageDepartments={manageDepartments}
                                                                        />
                                                                    )
                                                                })
                                                            ) ?? []
                                                        }
                                                        optionLabelProp="name"
                                                    />
                                                </Form.Item>
                                            </div>

                                            {isLoadingChangingAccountProfile && <Spin />}

                                            <div
                                                style={{
                                                    display: "flex",
                                                    width: "100%",
                                                    justifyContent: "flex-end",
                                                    flexWrap: "wrap",
                                                    gap: "5px"
                                                }}>
                                                <div
                                                    onClick={() =>
                                                        me.isCompanyMaster
                                                            ? setUpdateCompanyInfoModalVisibility(true)
                                                            : setUpdateSubAdminInfo(me)
                                                    }
                                                    className="company-profile-budget-layout"
                                                    style={{cursor: "pointer"}}>
                                                    <span className="company-profile-budget-text">
                                                        {"עריכת פרטי חשבון"}
                                                    </span>
                                                </div>

                                                {!!me?.isPrimaryAccount && (
                                                    <div
                                                        onClick={() => setChangePasswordModalVisibility(true)}
                                                        className="company-profile-budget-layout"
                                                        style={{cursor: "pointer"}}>
                                                        <span className="company-profile-budget-text">
                                                            {"שינוי סיסמא"}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="company-profile-info-box-layout">
                                            <div style={{display: "flex", flexDirection: "column"}}>
                                                <span
                                                    className="company-profile-header"
                                                    style={{fontSize: 24, direction: "rtl"}}>
                                                    {new StringBuilder()
                                                        .append("יתרת תקציב")
                                                        .append(me?.isCompanyMaster ? "של הארגון" : "")
                                                        .append(me?.manageDepartments?.length ? "של המחלקה" : "")
                                                        .toString()}
                                                    <Tooltip
                                                        title={new StringBuilder()
                                                            .append("סך הסכום הזמין עבורך")
                                                            .append(isManager ? "ועבור המשתמשים שתחתייך" : "")
                                                            .toString()}>
                                                        <QuestionCircleOutlined className="company-profile-info-question-icon" />
                                                    </Tooltip>
                                                </span>
                                                {isNumber(remainingBudgets) ? (
                                                    <span className="company-profile-info-colored-text">
                                                        {remainingBudgets.toLocaleString()}₪
                                                    </span>
                                                ) : null}
                                                {isManager ? (
                                                    <span className="company-profile-info-text">
                                                        לשימוש {Object.keys(companyBudgetsByUserId ?? {}).length}{" "}
                                                        משתמשים
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    width: "100%"
                                                }}>
                                                <div className="company-profile-labels-layout">
                                                    <div
                                                        onClick={() => setBudgetHistoryModalVisibility(true)}
                                                        className="company-profile-budget-layout">
                                                        <span className="company-profile-budget-text">
                                                            {"היסטוריית תקציב"}
                                                        </span>
                                                    </div>
                                                    <CompanyBudgetLabel me={me} company={company} services={services} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <span className="company-profile-header" style={{fontSize: 24}}>
                                        הזמנות פתוחות
                                    </span>
                                    <RoundedTable
                                        rowKey="eventId"
                                        pagination={false}
                                        style={{marginTop: "20px"}}
                                        scroll={{x: 1400}}
                                        loading={isOrdersLoading}
                                        dataSource={orders}
                                        columns={columns(me, services, onEditOrder)}
                                    />
                                </>
                            )
                        },
                        {
                            label: "ניהול הזמנות",
                            key: "2",
                            children: <CompanyOrdersWrapper withHeader={false} match={match} />
                        },
                        isManager && !!me?.features?.company_profile_manage_admin
                            ? {
                                  label: "ניהול אדמינים",
                                  className: "companyProfileManageAdmin",
                                  key: "3",
                                  children: (
                                      <CompanyProfileManageAdmins
                                          budgets={companyBudgets?.budgets}
                                          fetchBudgets={updateBudget}
                                          budgetLoading={loadingCompanyBudgets}
                                          history={history}
                                          services={services}
                                      />
                                  )
                              }
                            : null,
                        isAllowedToInvoiceTab
                            ? {
                                  label: "ניהול חשבוניות",
                                  key: "4",
                                  children: (
                                      <CompanyInvoiceManage
                                          onSave={onSaveMeCompanyFinanceContact}
                                          me={me}
                                          company={company}
                                      />
                                  )
                              }
                            : null,
                        {
                            label: "ניהול טיוטות הזמנה",
                            key: "5",
                            children: <SavedOrderTable history={history} />
                        },
                        ...(reports?.length
                            ? [
                                  {
                                      label: "דוחות",
                                      key: "6",
                                      children: (
                                          <ReportManagement
                                              companyId={match?.params?.companyId}
                                              reports={reports}
                                              history={history}
                                          />
                                      )
                                  }
                              ]
                            : []),
                        me?.features?.brif
                            ? {
                                  label: "ניהול בריפים",
                                  key: "7",
                                  children: <CompanyBriefsWrapper />
                              }
                            : null
                    ]}></Tabs>
            </ConfigProvider>
        </div>
    );
};

const UserCrown = ({firstName, isCompanyMaster, manageDepartments}) => {
    return (
        <div>
            <span className="user-crown-firstName">{firstName}</span>
            {isCompanyMaster && <CrownFilled className="sub-admin-table-crown" />}
            {manageDepartments?.length && (
                <CrownFilled className={classNames("sub-admin-table-crown", "manage-departments")} />
            )}
        </div>
    );
};
